import React, { useEffect, useState } from 'react';
import { api } from '../lib/apiClient';
import Navbar from '../components/Navbar';

const NAVY = '#1A2B4C';
const GOLD = '#FCD359';
const GOLD_DARK = '#E0B73D';

const DigestPreviewPage = () => {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [recipientsOverride, setRecipientsOverride] = useState('');

  useEffect(() => {
    let cancel = false;
    const load = async () => {
      setLoading(true); setErr('');
      try {
        const [previewRes, historyRes] = await Promise.all([
          api.get(`/api/admin/digest/preview?days=${days}`),
          api.get('/api/admin/digest/sends?limit=10'),
        ]);
        if (cancel) return;
        setData(previewRes.data);
        setHistory(historyRes.data?.sends || []);
      } catch (e) {
        if (!cancel) setErr(e.response?.data?.detail || 'Failed to load digest preview.');
      } finally {
        if (!cancel) setLoading(false);
      }
    };
    load();
    return () => { cancel = true; };
  }, [days]);

  const send = async (dryRun) => {
    setSending(true); setSendResult(null);
    try {
      const recipients = recipientsOverride.trim()
        ? recipientsOverride.split(',').map((s) => s.trim()).filter(Boolean)
        : null;
      const { data: r } = await api.post('/api/admin/digest/send', {
        days, dry_run: dryRun, recipients,
      });
      setSendResult(r);
      if (!dryRun && r.sent) {
        const h = await api.get('/api/admin/digest/sends?limit=10');
        setHistory(h.data?.sends || []);
      }
    } catch (e) {
      setSendResult({ error: e.response?.data?.detail || 'Send failed.' });
    } finally {
      setSending(false);
    }
  };

  const p = data?.payload;

  return (
    <div className="min-h-screen" style={{ background: 'var(--gr-bone-2)' }}>
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-6" data-testid="digest-preview-page">
        <div className="flex items-end justify-between gap-3 flex-wrap mb-4">
          <div>
            <h1 className="text-2xl font-bold" style={{ color: NAVY }} data-testid="digest-title">
              Faculty Authenticity Digest
            </h1>
            <div className="text-sm text-gray-600 mt-1 max-w-2xl">
              Weekly summary email sent to faculty / admins. Preview what they&apos;ll receive,
              send a dry-run to yourself, or fire to the whole faculty list.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-xs uppercase tracking-wider font-bold text-gray-500">Window</label>
            <select value={days} onChange={(e) => setDays(parseInt(e.target.value, 10))}
                    className="input-field py-1 px-2 text-sm" data-testid="digest-window-select">
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={60}>Last 60 days</option>
            </select>
          </div>
        </div>

        {/* KPIs */}
        {p && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <Kpi label="Total" value={p.total} accent={NAVY} />
            <Kpi label="Authentic" value={p.authentic} accent="#16A34A" />
            <Kpi label="Watch" value={p.watch} accent="#F59E0B" />
            <Kpi label="High concern" value={p.high_concern} accent="#DC2626" />
            <Kpi label="Concern rate" value={`${p.high_concern_rate}%`} accent={GOLD_DARK} />
          </div>
        )}

        {/* Send controls */}
        <div className="card p-4 mb-4">
          <div className="flex items-end gap-3 flex-wrap">
            <div className="flex-1 min-w-[280px]">
              <label className="text-xs uppercase tracking-wider font-bold text-gray-500 block mb-1">
                Override recipients (comma-separated) — leave blank to send to all faculty
              </label>
              <input
                type="text"
                value={recipientsOverride}
                onChange={(e) => setRecipientsOverride(e.target.value)}
                placeholder="dean@example.edu, prof@example.edu"
                className="input-field"
                data-testid="digest-recipients-input"
              />
            </div>
            <div className="flex gap-2">
              <button onClick={() => send(true)} disabled={sending}
                      className="btn-outline text-sm"
                      data-testid="digest-dry-run-btn">
                Preview (dry run)
              </button>
              <button onClick={() => send(false)} disabled={sending}
                      className="btn-primary text-sm"
                      data-testid="digest-send-btn">
                {sending ? 'Sending…' : 'Send digest now'}
              </button>
            </div>
          </div>

          {sendResult && (
            <div className="mt-4 p-3 rounded text-sm" style={{
              background: sendResult.error ? '#FEF2F2' : (sendResult.sent ? '#ECFDF5' : '#FFFBEB'),
              border: `1px solid ${sendResult.error ? '#FCA5A5' : (sendResult.sent ? '#86EFAC' : '#FCD34D')}`,
            }} data-testid="digest-send-result">
              {sendResult.error ? (
                <span className="font-bold" style={{ color: '#B91C1C' }}>Error: {sendResult.error}</span>
              ) : sendResult.sent ? (
                <span style={{ color: '#065F46' }}>
                  ✓ Sent to {sendResult.succeeded} of {sendResult.recipients_count} faculty
                  {sendResult.failed_count > 0 && ` · ${sendResult.failed_count} failures`}
                </span>
              ) : (
                <span style={{ color: '#92400E' }}>
                  {sendResult.reason === 'no_resend_key'
                    ? `🔑 Not sent — no RESEND_API_KEY configured. Would send to ${sendResult.recipients_count} recipients.`
                    : `Dry run complete — would send to ${sendResult.recipients_count} recipients (${(sendResult.recipients_preview || []).join(', ')}${sendResult.recipients_count > 5 ? ', …' : ''}).`}
                </span>
              )}
            </div>
          )}
        </div>

        {/* HTML preview iframe */}
        <div className="card p-4 mb-4">
          <div className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color: NAVY }}>
            Email preview
          </div>
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading…</div>
          ) : err ? (
            <div className="p-4 text-red-600">{err}</div>
          ) : data ? (
            <iframe
              title="Digest preview"
              srcDoc={data.html}
              style={{ width: '100%', height: 820, border: '1px solid var(--gr-border)', borderRadius: 8, background: '#F7F3EA' }}
              data-testid="digest-iframe"
            />
          ) : null}
        </div>

        {/* Send history */}
        <div className="card p-4">
          <div className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color: NAVY }}>
            Recent sends
          </div>
          {history.length === 0 ? (
            <div className="text-gray-500 text-sm">No digest has been sent yet.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 text-xs uppercase tracking-wider">
                  <th className="pb-2">When</th>
                  <th className="pb-2">Sent by</th>
                  <th className="pb-2 text-right">Window</th>
                  <th className="pb-2 text-right">Recipients</th>
                  <th className="pb-2 text-right">Succeeded</th>
                  <th className="pb-2 text-right">Failed</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h, i) => (
                  <tr key={i} className="border-t" style={{ borderColor: 'var(--gr-border)' }}>
                    <td className="py-2 text-xs">{(h.sent_at || '').slice(0, 16).replace('T', ' ')} UTC</td>
                    <td className="py-2 text-xs">{h.sent_by}</td>
                    <td className="py-2 text-right">{h.window_days}d</td>
                    <td className="py-2 text-right">{h.recipients_count}</td>
                    <td className="py-2 text-right font-bold" style={{ color: '#16A34A' }}>{h.succeeded}</td>
                    <td className="py-2 text-right font-bold"
                        style={{ color: h.failed_count > 0 ? '#DC2626' : NAVY }}>{h.failed_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
};

const Kpi = ({ label, value, accent }) => (
  <div className="card p-4">
    <div className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">{label}</div>
    <div className="text-2xl font-bold mt-1" style={{ color: accent || NAVY }}>{value}</div>
  </div>
);

export default DigestPreviewPage;
